import { useQuery } from '@tanstack/react-query';
import { formatSessionWallTime, sessionWallClockMs } from '@lighthouse/shared';
import { api } from '../services/api';

export interface NotificationItem {
  id: string;
  bookingId: string;
  title: string;
  body: string;
  scheduledAt: string;
}

interface BookingRow {
  id: string;
  scheduledAt: string;
  status: string;
  therapist?: { displayName: string };
  client?: { displayName: string };
}

function toNotificationItems(bookings: BookingRow[]): NotificationItem[] {
  const now = Date.now();
  const weekAhead = now + 7 * 24 * 60 * 60 * 1000;

  return bookings
    .filter((b) => {
      const at = sessionWallClockMs(b.scheduledAt);
      return b.status === 'confirmed' && at > now && at <= weekAhead;
    })
    .sort((a, b) => sessionWallClockMs(a.scheduledAt) - sessionWallClockMs(b.scheduledAt))
    .map((b) => {
      const when = formatSessionWallTime(
        b.scheduledAt,
        {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        },
        'en-NG'
      );
      const withName = b.therapist?.displayName ?? b.client?.displayName ?? 'your session';
      return {
        id: `session-${b.id}`,
        bookingId: b.id,
        title: 'Upcoming session',
        body: `Session with ${withName} on ${when}`,
        scheduledAt: b.scheduledAt,
      };
    });
}

export function useNotificationFeed(enabled = true) {
  return useQuery<NotificationItem[]>({
    queryKey: ['notification-feed'],
    queryFn: async () => {
      const bookings = await api.get<BookingRow[]>('/bookings/my');
      return toNotificationItems(bookings);
    },
    enabled,
    staleTime: 60_000,
  });
}
